/**
 * Supabase client unico do AraFit.
 *
 * Em PRODUCAO: `supabase` e o cliente prod direto (sem proxy, sem demo).
 * Em DEV (__DEV__): `supabase` e um Proxy que pode switchar pra demo
 * client via switchToDemo() (usado em login.tsx com demo-personal@).
 *
 * O cliente demo vive em `lib/supabase-demo.ts` e e carregado via require()
 * dinamico dentro de `if (__DEV__)` — bundle de producao nao deve inclui-lo
 * (verificar com `curl <bundle> | grep wpyogisjlzkxxqguuiuz` apos deploy).
 *
 * Defense in depth (mesmo se o tree-shaking falhar):
 *   1. Caminhos demo sao no-op em prod (`if (!__DEV__) return`)
 *   2. signIn em hooks/useAuth rejeita explicitamente o email demo em prod
 *   3. Conta `demo-personal@arafit.com.br` sera apagada do banco PROD
 */
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';

const clean = (v?: string) => (v ?? '').replace(/[^\x20-\x7E]/g, '');

const PROD_URL =
  clean(process.env.EXPO_PUBLIC_SUPABASE_URL) ||
  'https://sdqdhpjmecqrrhztqhbc.supabase.co';
const PROD_KEY =
  clean(process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY) ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNkcWRocGptZWNxcnJoenRxaGJjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxMDc2NzMsImV4cCI6MjA5MzY4MzY3M30.IQdLxOLUmOpP_xB0KE1_7eyye5Tl2PEtTj83WQZ4WVo';

/** Email da conta demo. Const exposta pra que useAuth bloqueie em prod. */
export const DEMO_ACCOUNT_EMAIL = 'demo-personal@arafit.com.br';

function makeStorage() {
  if (Platform.OS === 'web') return undefined;
  return {
    getItem: (key: string) => SecureStore.getItemAsync(key),
    setItem: (key: string, value: string) => SecureStore.setItemAsync(key, value),
    removeItem: (key: string) => SecureStore.deleteItemAsync(key),
  };
}

/**
 * Factory de cliente Supabase. Exportada pra que `lib/supabase-demo.ts`
 * reutilize as mesmas auth options (storage, autoRefreshToken, etc).
 */
export function makeClient(url: string, key: string): SupabaseClient {
  return createClient(url, key, {
    auth: {
      storage: makeStorage(),
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
  });
}

const prodClient = makeClient(PROD_URL, PROD_KEY);

// Active client used by the Proxy in dev. In prod, supabase is prodClient direto.
let _activeClient: SupabaseClient = prodClient;

/**
 * Cliente Supabase para uso em hooks/telas.
 *
 * - Em PROD: e o `prodClient` direto. Imutavel.
 * - Em DEV: e um Proxy que delega pro `_activeClient` (pode ser prod ou demo).
 *
 * Hooks que importam `supabase` automaticamente pegam o cliente ativo
 * apos `switchToDemo()` em dev. Em prod, switchToDemo e no-op.
 */
export const supabase: SupabaseClient = __DEV__
  ? new Proxy({} as SupabaseClient, {
      get(_, prop) {
        const val = (_activeClient as unknown as Record<string | symbol, unknown>)[prop];
        return typeof val === 'function' ? (val as (...args: unknown[]) => unknown).bind(_activeClient) : val;
      },
    })
  : prodClient;

// =====================================================================
// Demo helpers — DEV-ONLY. No-op em build de producao.
// =====================================================================

type SessionListener = (session: Session | null) => void;
const _demoSessionListeners: SessionListener[] = [];

/**
 * Switcha o cliente ativo para o demo. No-op em prod.
 * Lazy-loads `lib/supabase-demo.ts` via require() pra tree-shaking.
 */
export function switchToDemo(): void {
  if (!__DEV__) return;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const demo = require('./supabase-demo') as typeof import('./supabase-demo');
  _activeClient = demo.getDemoClient(makeClient);
}

/** Volta o cliente ativo para prod. No-op em prod (sempre foi prod). */
export function switchToProd(): void {
  if (!__DEV__) return;
  _activeClient = prodClient;
}

/**
 * Retorna o cliente demo direto (usado por useAuth.signIn em dev pra fazer
 * o auth do demo sem depender do Proxy). Em prod, retorna prodClient (defensive
 * — nunca deve ser chamado em prod porque signIn rejeita demo antes).
 */
export function getDemoClient(): SupabaseClient {
  if (!__DEV__) return prodClient;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const demo = require('./supabase-demo') as typeof import('./supabase-demo');
  return demo.getDemoClient(makeClient);
}

/**
 * Assina um listener pra mudancas de sessao demo (login/logout) em DEV.
 * No-op em prod (retorna unsubscribe vazio).
 */
export function onDemoSessionChange(cb: SessionListener): () => void {
  if (!__DEV__) return () => {};
  _demoSessionListeners.push(cb);
  return () => {
    const i = _demoSessionListeners.indexOf(cb);
    if (i >= 0) _demoSessionListeners.splice(i, 1);
  };
}

/**
 * Emite mudanca de sessao demo pra todos os listeners. No-op em prod.
 */
export function emitDemoSession(session: Session | null): void {
  if (!__DEV__) return;
  _demoSessionListeners.forEach((cb) => cb(session));
}
